/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from 'react';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Trash2,
  SkipBack,
  SkipForward,
  ZoomIn,
  ZoomOut,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Plus,
} from 'lucide-react';

interface AudioTrack {
  id: string;
  type: 'music' | 'sfx';
  title: string;
  startTime: number;
  duration: number;
  trimStart: number;
  trimEnd: number;
  color: string;
  volume: number;
  isMuted: boolean;
  isLocked: boolean;
  waveform?: number[];
}

interface Track {
  id: string;
  name: string;
  type: 'video' | 'audio';
  isVisible: boolean;
  isLocked: boolean;
  volume: number;
  isMuted: boolean;
  items: AudioTrack[];
}

interface TimelineEditorProps {
  videoDuration: number;
  uploadedAudio?: any;
  uploadedVideo?: File | null;
  onAudioUpdate?: (blocks: AudioTrack[]) => void;
}

export default function TimelineEditor({
  videoDuration = 60,
  uploadedAudio,
  uploadedVideo,
  onAudioUpdate,
}: TimelineEditorProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [zoom, setZoom] = useState(1); // 0.5 to 3
  const [tracks, setTracks] = useState<Track[]>([
    {
      id: 'video-track',
      name: 'Video',
      type: 'video',
      isVisible: true,
      isLocked: false,
      volume: 100,
      isMuted: false,
      items: [],
    },
  ]);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState<'move' | 'trim-start' | 'trim-end' | null>(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartTime, setDragStartTime] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [actualVideoDuration, setActualVideoDuration] = useState(videoDuration);
  const [, setScrollLeft] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const timelineScrollRef = useRef<HTMLDivElement>(null);

  // Constants
  const PIXELS_PER_SECOND = 40 * zoom;
  const TRACK_HEIGHT = 64;
  // const TRACK_LABEL_WIDTH = 180;
  // const RULER_HEIGHT = 32;

  // Handle uploaded video
  useEffect(() => {
    if (uploadedVideo) {
      const url = URL.createObjectURL(uploadedVideo);
      setVideoUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setVideoUrl(null);
    }
  }, [uploadedVideo]);

  // Get actual video duration
  useEffect(() => {
    if (videoRef.current) {
      const handleLoadedMetadata = () => {
        if (videoRef.current) {
          setActualVideoDuration(videoRef.current.duration);
        }
      };
      videoRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      return () => {
        videoRef.current?.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [videoUrl]);

  // Sync video playback
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = currentTime;
    }
  }, [currentTime]);

  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.play().catch(() => setIsPlaying(false));
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying]);

  // Play/Pause simulation for when no video
  useEffect(() => {
    if (!isPlaying || videoUrl) return;
    const interval = setInterval(() => {
      setCurrentTime((prev) => {
        if (prev >= actualVideoDuration) {
          setIsPlaying(false);
          return 0;
        }
        return prev + 0.1;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [isPlaying, actualVideoDuration, videoUrl]);

  // Sync timeline with video
  useEffect(() => {
    if (!videoRef.current || !videoUrl) return;
    const handleTimeUpdate = () => {
      if (videoRef.current && isPlaying) {
        setCurrentTime(videoRef.current.currentTime);
      }
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    videoRef.current.addEventListener('timeupdate', handleTimeUpdate);
    videoRef.current.addEventListener('ended', handleEnded);
    return () => {
      videoRef.current?.removeEventListener('timeupdate', handleTimeUpdate);
      videoRef.current?.removeEventListener('ended', handleEnded);
    };
  }, [videoUrl, isPlaying]);

  // Convert uploaded audio to track item
  useEffect(() => {
    if (!uploadedAudio) return;

    // Check if already added
    const alreadyExists = tracks.some((track) => track.items.some((item) => item.title === uploadedAudio.title));
    if (alreadyExists) return;

    const newItem: AudioTrack = {
      id: `audio-${Date.now()}`,
      type: uploadedAudio.category ? 'sfx' : 'music',
      title: uploadedAudio.title,
      startTime: 0,
      duration: uploadedAudio.duration
        ? parseFloat(uploadedAudio.duration.split(':')[0]) * 60 + parseFloat(uploadedAudio.duration.split(':')[1])
        : 30,
      trimStart: 0,
      trimEnd: 0,
      color: uploadedAudio.category ? '#8B5CF6' : '#F67570',
      volume: 100,
      isMuted: false,
      isLocked: false,
      waveform: uploadedAudio.waveform || generateWaveform(32),
    };

    // Find or create audio track
    let audioTrack = tracks.find((t) => t.type === 'audio' && t.name === 'Audio 1');
    if (!audioTrack) {
      audioTrack = {
        id: `audio-track-${Date.now()}`,
        name: 'Audio 1',
        type: 'audio',
        isVisible: true,
        isLocked: false,
        volume: 100,
        isMuted: false,
        items: [],
      };
      setTracks([...tracks, audioTrack]);
    }

    setTracks((prevTracks) =>
      prevTracks.map((track) => (track.id === audioTrack!.id ? { ...track, items: [...track.items, newItem] } : track))
    );

    onAudioUpdate?.([...audioTrack.items, newItem]);
  }, [uploadedAudio]);

  const generateWaveform = (length: number) => {
    return Array.from({ length }, () => Math.random() * 0.8 + 0.2);
  };

  const timeToPixels = (time: number) => time * PIXELS_PER_SECOND;
  const pixelsToTime = (pixels: number) => pixels / PIXELS_PER_SECOND;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => setIsPlaying(!isPlaying);
  const skipToStart = () => setCurrentTime(0);
  const skipToEnd = () => setCurrentTime(actualVideoDuration);

  const handleZoomIn = () => setZoom(Math.min(3, zoom + 0.25));
  const handleZoomOut = () => setZoom(Math.max(0.5, zoom - 0.25));

  const addAudioTrack = () => {
    const trackNumber = tracks.filter((t) => t.type === 'audio').length + 1;
    const newTrack: Track = {
      id: `audio-track-${Date.now()}`,
      name: `Audio ${trackNumber}`,
      type: 'audio',
      isVisible: true,
      isLocked: false,
      volume: 100,
      isMuted: false,
      items: [],
    };
    setTracks([...tracks, newTrack]);
  };

  const toggleTrackMute = (trackId: string) => {
    setTracks(tracks.map((track) => (track.id === trackId ? { ...track, isMuted: !track.isMuted } : track)));
  };

  const toggleTrackLock = (trackId: string) => {
    setTracks(tracks.map((track) => (track.id === trackId ? { ...track, isLocked: !track.isLocked } : track)));
  };

  const toggleTrackVisibility = (trackId: string) => {
    setTracks(tracks.map((track) => (track.id === trackId ? { ...track, isVisible: !track.isVisible } : track)));
  };

  const deleteItem = (trackId: string, itemId: string) => {
    setTracks(
      tracks.map((track) =>
        track.id === trackId ? { ...track, items: track.items.filter((item) => item.id !== itemId) } : track
      )
    );
    if (selectedItemId === itemId) {
      setSelectedItemId(null);
    }
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!timelineRef.current || isDragging) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = pixelsToTime(clickX);
    setCurrentTime(Math.max(0, Math.min(actualVideoDuration, newTime)));
  };

  // Drag handlers
  const startDrag = (
    e: React.MouseEvent,
    itemId: string,
    trackId: string,
    type: 'move' | 'trim-start' | 'trim-end'
  ) => {
    const track = tracks.find((t) => t.id === trackId);
    if (!track || track.isLocked) return;

    e.stopPropagation();
    setSelectedItemId(itemId);
    setIsDragging(true);
    setDragType(type);
    setDragStartX(e.clientX);

    const item = track.items.find((i) => i.id === itemId);
    if (item) {
      setDragStartTime(
        type === 'trim-end' ? item.startTime + item.duration - item.trimStart - item.trimEnd : item.startTime
      );
    }
  };

  useEffect(() => {
    if (!isDragging || !selectedItemId || !dragType) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartX;
      const deltaTime = pixelsToTime(Math.abs(deltaX)) * (deltaX >= 0 ? 1 : -1);

      setTracks((prevTracks) =>
        prevTracks.map((track) => ({
          ...track,
          items: track.items.map((item) => {
            if (item.id !== selectedItemId) return item;

            if (dragType === 'move') {
              const newStartTime = Math.max(
                0,
                Math.min(
                  actualVideoDuration - (item.duration - item.trimStart - item.trimEnd),
                  dragStartTime + deltaTime
                )
              );
              return { ...item, startTime: newStartTime };
            } else if (dragType === 'trim-start') {
              const maxTrim = item.duration - item.trimEnd - 0.1;
              const newTrimStart = Math.max(0, Math.min(maxTrim, item.trimStart + deltaTime));
              const newStartTime = Math.max(0, dragStartTime + deltaTime);
              return { ...item, trimStart: newTrimStart, startTime: newStartTime };
            } else if (dragType === 'trim-end') {
              const maxTrim = item.duration - item.trimStart - 0.1;
              const newTrimEnd = Math.max(0, Math.min(maxTrim, item.trimEnd - deltaTime));
              return { ...item, trimEnd: newTrimEnd };
            }
            return item;
          }),
        }))
      );
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setDragType(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, selectedItemId, dragType, dragStartX, dragStartTime, actualVideoDuration, PIXELS_PER_SECOND]);

  const timelineWidth = timeToPixels(actualVideoDuration);
  const playheadPosition = timeToPixels(currentTime);

  return (
    <div className="w-full bg-white border border-[#E5E7EB] rounded-lg shadow-sm overflow-hidden">
      {/* Header with Controls */}
      <div className="px-4 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Playback Controls */}
            <div className="flex items-center gap-1">
              <button
                onClick={skipToStart}
                className="w-8 h-8 rounded hover:bg-gray-100 flex items-center justify-center text-[#6B7280] transition-colors"
                title="Skip to start"
              >
                <SkipBack size={16} />
              </button>
              <button
                onClick={handlePlayPause}
                className="w-10 h-10 rounded-lg bg-gradient-to-r from-[#F67570] to-[#F99B97] hover:shadow-lg text-white flex items-center justify-center transition-all"
                title={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
              </button>
              <button
                onClick={skipToEnd}
                className="w-8 h-8 rounded hover:bg-gray-100 flex items-center justify-center text-[#6B7280] transition-colors"
                title="Skip to end"
              >
                <SkipForward size={16} />
              </button>
            </div>

            {/* Time Display */}
            <div className="text-sm font-mono text-[#111827] bg-white px-3 py-1.5 rounded border border-[#E5E7EB]">
              {formatTime(currentTime)} / {formatTime(actualVideoDuration)}
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-2 pl-4 border-l border-[#E5E7EB]">
              <button
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
                className="w-8 h-8 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-[#6B7280] transition-colors"
                title="Zoom out"
              >
                <ZoomOut size={16} />
              </button>
              <div className="text-xs font-medium text-[#6B7280] w-12 text-center">{Math.round(zoom * 100)}%</div>
              <button
                onClick={handleZoomIn}
                disabled={zoom >= 3}
                className="w-8 h-8 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-[#6B7280] transition-colors"
                title="Zoom in"
              >
                <ZoomIn size={16} />
              </button>
            </div>
          </div>

          {/* Video Preview Toggle */}
          {videoUrl && (
            <div className="flex items-center gap-2">
              <div className="text-xs text-[#6B7280]">Video preview</div>
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
            </div>
          )}
        </div>
      </div>

      {/* Timeline Area */}
      <div className="flex h-[400px]">
        {/* Track Labels Sidebar */}
        <div className="w-[180px] border-r border-[#E5E7EB] bg-[#F9FAFB] flex-shrink-0">
          {/* Ruler Space */}
          <div className="h-[32px] border-b border-[#E5E7EB] flex items-center px-3">
            <span className="text-xs font-medium text-[#6B7280]">Tracks</span>
          </div>

          {/* Track Labels */}
          <div className="overflow-y-auto" style={{ height: `calc(100% - 32px)` }}>
            {tracks.map((track) => (
              <div
                key={track.id}
                className="h-[64px] border-b border-[#E5E7EB] px-3 py-2 flex flex-col justify-between bg-white"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-[#111827] flex-1 truncate">{track.name}</span>
                  <button
                    onClick={() => toggleTrackVisibility(track.id)}
                    className="w-5 h-5 rounded hover:bg-gray-100 flex items-center justify-center text-[#6B7280]"
                    title={track.isVisible ? 'Hide track' : 'Show track'}
                  >
                    {track.isVisible ? <Eye size={12} /> : <EyeOff size={12} />}
                  </button>
                  <button
                    onClick={() => toggleTrackLock(track.id)}
                    className="w-5 h-5 rounded hover:bg-gray-100 flex items-center justify-center text-[#6B7280]"
                    title={track.isLocked ? 'Unlock track' : 'Lock track'}
                  >
                    {track.isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleTrackMute(track.id)}
                    className="w-5 h-5 rounded hover:bg-gray-100 flex items-center justify-center text-[#6B7280]"
                    title={track.isMuted ? 'Unmute' : 'Mute'}
                  >
                    {track.isMuted ? <VolumeX size={12} /> : <Volume2 size={12} />}
                  </button>
                  <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#F67570] rounded-full"
                      style={{ width: `${track.isMuted ? 0 : track.volume}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}

            {/* Add Track Button */}
            <button
              onClick={addAudioTrack}
              className="w-full h-[48px] border-b border-[#E5E7EB] px-3 flex items-center gap-2 hover:bg-gray-50 text-[#6B7280] transition-colors"
            >
              <Plus size={14} />
              <span className="text-xs font-medium">Add Audio Track</span>
            </button>
          </div>
        </div>

        {/* Timeline Scroll Area */}
        <div className="flex-1 overflow-hidden relative">
          {/* Time Ruler */}
          <div className="h-[32px] border-b border-[#E5E7EB] bg-white relative overflow-hidden">
            <div
              ref={timelineScrollRef}
              className="overflow-x-auto overflow-y-hidden h-full scrollbar-thin"
              onScroll={(e) => setScrollLeft(e.currentTarget.scrollLeft)}
            >
              <div className="h-full relative" style={{ width: `${timelineWidth}px`, minWidth: '100%' }}>
                {/* Time markers */}
                {Array.from({ length: Math.ceil(actualVideoDuration) + 1 }, (_, i) => i).map((second) => (
                  <div
                    key={second}
                    className="absolute top-0 h-full flex flex-col justify-end"
                    style={{ left: `${timeToPixels(second)}px` }}
                  >
                    <div className="w-px h-2 bg-[#D1D5DB]"></div>
                    {second % 5 === 0 && (
                      <span className="absolute top-1 -translate-x-1/2 text-[10px] font-mono text-[#6B7280]">
                        {formatTime(second)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tracks Timeline */}
          <div
            className="overflow-auto scrollbar-thin"
            style={{ height: `calc(100% - 32px)` }}
            onScroll={(e) => {
              if (timelineScrollRef.current) {
                timelineScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
              }
            }}
          >
            <div
              ref={timelineRef}
              className="relative"
              style={{ width: `${timelineWidth}px`, minWidth: '100%', minHeight: '100%' }}
              onClick={handleTimelineClick}
            >
              {/* Track Rows */}
              {tracks.map((track, trackIndex) => (
                <div
                  key={track.id}
                  className="h-[64px] border-b border-[#E5E7EB] relative"
                  style={{
                    backgroundColor: trackIndex % 2 === 0 ? '#FFFFFF' : '#F9FAFB',
                    opacity: track.isVisible ? 1 : 0.4,
                  }}
                >
                  {/* Video Track Content */}
                  {track.type === 'video' && videoUrl && (
                    <div
                      className="absolute top-1 left-0 h-[56px] bg-gradient-to-r from-blue-100 to-purple-100 border-2 border-blue-300 rounded overflow-hidden"
                      style={{
                        width: `${timeToPixels(actualVideoDuration)}px`,
                      }}
                    >
                      <div className="h-full flex items-center justify-center bg-blue-500/10">
                        <div className="text-xs font-medium text-blue-900 px-2 truncate">
                          {uploadedVideo?.name || 'Video'}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Audio Items */}
                  {track.items.map((item) => {
                    const itemWidth = timeToPixels(item.duration - item.trimStart - item.trimEnd);
                    const itemLeft = timeToPixels(item.startTime + item.trimStart);
                    const isSelected = selectedItemId === item.id;

                    return (
                      <div
                        key={item.id}
                        className={`absolute top-1 h-[56px] rounded border-2 cursor-move transition-all ${
                          isSelected ? 'border-white shadow-lg z-10' : 'border-transparent'
                        } ${track.isLocked ? 'cursor-not-allowed opacity-60' : ''}`}
                        style={{
                          left: `${itemLeft}px`,
                          width: `${itemWidth}px`,
                          backgroundColor: item.color,
                          minWidth: '40px',
                        }}
                        onMouseDown={(e) => !track.isLocked && startDrag(e, item.id, track.id, 'move')}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedItemId(item.id);
                        }}
                      >
                        {/* Waveform */}
                        <div className="h-full flex items-center px-2 gap-0.5 overflow-hidden">
                          {item.waveform?.map((height, i) => (
                            <div
                              key={i}
                              className="flex-1 bg-white/40 rounded-full min-w-[1px]"
                              style={{ height: `${height * 70}%` }}
                            />
                          ))}
                        </div>

                        {/* Title Overlay */}
                        <div className="absolute inset-0 flex items-center px-2 pointer-events-none">
                          <span className="text-xs font-medium text-white drop-shadow truncate">{item.title}</span>
                        </div>

                        {/* Trim Handles */}
                        {!track.isLocked && (
                          <>
                            <div
                              className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 z-20"
                              onMouseDown={(e) => startDrag(e, item.id, track.id, 'trim-start')}
                            />
                            <div
                              className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 z-20"
                              onMouseDown={(e) => startDrag(e, item.id, track.id, 'trim-end')}
                            />
                          </>
                        )}

                        {/* Delete Button (when selected) */}
                        {isSelected && !track.isLocked && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteItem(track.id, item.id);
                            }}
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg z-30 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Playhead */}
              <div
                className="absolute top-0 w-px bg-[#F67570] pointer-events-none z-20"
                style={{
                  left: `${playheadPosition}px`,
                  height: `${tracks.length * TRACK_HEIGHT}px`,
                }}
              >
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#F67570] shadow-md" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden Video Element */}
      {videoUrl && <video ref={videoRef} src={videoUrl} className="hidden" playsInline />}
    </div>
  );
}
